<template>
	<v-app style="background-color: #f4fafa">
		<!-- Header -->
		<v-app-bar app flat dark height="74" class="header-bar">
			<v-img
				alt="Orixe Logo"
				:src="require('../../assets/Logo_Header_White.svg')"
				max-width="170"
				contain
			/>
			<div class="header-divider mx-4"></div>
			<span class="header-title">Incident Reporting Platform</span>
			<v-spacer></v-spacer>
			<span class="header-title manager-view-title">Manager View</span>
			<v-btn text class="add-btn mr-3" @click="addIncident">Add Incident</v-btn>
			<v-btn outlined tile class="logout-btn" @click="logout">Log Out</v-btn>
		</v-app-bar>

		<v-main>
			<v-container class="page-container">
				<h1 class="page-title">Incidents</h1>
				<p class="page-subtitle mb-8">
					Review reported incidents, record the consequence and action point, and
					update the status.
				</p>

				<!-- Summary cards -->
				<div class="summary-row mb-8">
					<div class="summary-card open">
						<div class="summary-count">{{ counts.open }}</div>
						<div class="summary-label">Open</div>
					</div>
					<div class="summary-card progress">
						<div class="summary-count">{{ counts.inProgress }}</div>
						<div class="summary-label">In progress</div>
					</div>
					<div class="summary-card resolved">
						<div class="summary-count">{{ counts.resolved }}</div>
						<div class="summary-label">Resolved</div>
					</div>
				</div>

				<!-- Filters -->
				<v-row dense class="mb-4">
					<v-col cols="12" md>
						<v-text-field
							v-model="search"
							placeholder="Search by ID, type or description"
							outlined
							light
							dense
							hide-details
							background-color="white"
							class="filter-field"
						></v-text-field>
					</v-col>
					<v-col cols="6" md="auto" class="select-col">
						<v-select
							v-model="typeFilter"
							:items="typeOptions"
							outlined
							light
							dense
							hide-details
							background-color="white"
							class="filter-field"
						></v-select>
					</v-col>
					<v-col cols="6" md="auto" class="select-col">
						<v-select
							v-model="statusFilter"
							:items="statusOptions"
							outlined
							light
							dense
							hide-details
							background-color="white"
							class="filter-field"
						></v-select>
					</v-col>
				</v-row>

				<!-- Table -->
				<v-data-table
					:headers="headers"
					:items="filteredIncidents"
					:loading="loading"
					hide-default-footer
					disable-pagination
					class="incident-table"
					no-data-text="No incidents found"
					@click:row="openIncident"
				>
					<template v-slot:item.id="{ item }">
						<b>{{ item.id }}</b>
					</template>
					<template v-slot:item.status="{ item }">
						<span :class="['status-pill', statusClass(item.status)]">
							{{ item.status }}
						</span>
					</template>
				</v-data-table>
			</v-container>
		</v-main>

		<!-- Detail / resolution side panel -->
		<v-navigation-drawer
			v-model="drawer"
			right
			temporary
			app
			width="520"
			class="detail-drawer"
		>
			<div class="drawer-header">
				<div>
					<div class="drawer-id">{{ selected.id }}</div>
					<div class="drawer-sub">Incident details and resolution</div>
				</div>
				<v-btn icon dark @click="closeDrawer">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</div>

			<div class="drawer-body">
				<!-- Reported by user (read-only) -->
				<h3 class="section-title">Reported by user</h3>
				<div class="reported-box">
					<div class="field-label">TYPE OF INCIDENT</div>
					<div class="field-value">{{ selected.type }}</div>

					<div class="field-label">DATE OF INCIDENT</div>
					<div class="field-value">{{ selected.date }}</div>

					<div class="field-label">DESCRIPTION</div>
					<div class="field-value mb-0">{{ selected.description }}</div>
				</div>

				<!-- Manager resolution (editable) -->
				<h3 class="section-title mt-8">Manager resolution</h3>
				<v-form ref="form">
					<label class="form-label">Consequence</label>
					<v-textarea
						v-model="form.consequence"
						outlined
						light
						rows="3"
						auto-grow
						hide-details
						background-color="white"
						class="mb-4"
					></v-textarea>

					<label class="form-label">Action Point</label>
					<v-textarea
						v-model="form.action"
						outlined
						light
						rows="3"
						auto-grow
						hide-details
						background-color="white"
						class="mb-4"
					></v-textarea>

					<label class="form-label">Responsible</label>
					<v-text-field
						v-model="form.responsible"
						outlined
						light
						dense
						hide-details
						background-color="white"
						class="mb-4"
					></v-text-field>

					<label class="form-label">Status</label>
					<v-select
						v-model="form.status"
						:items="drawerStatusOptions"
						outlined
						light
						dense
						hide-details
						background-color="white"
						class="mb-4"
					></v-select>

					<label class="form-label">Describe the routine for incident reporting, including how reviews are carried out</label>
					<v-textarea
						v-model="form.reviewRoutine"
						outlined
						light
						rows="3"
						auto-grow
						hide-details
						background-color="white"
						class="mb-4"
					></v-textarea>

					<label class="form-label">Describe the training routine</label>
					<v-textarea
						v-model="form.trainingRoutine"
						outlined
						light
						rows="3"
						auto-grow
						hide-details
						background-color="white"
						class="mb-6"
					></v-textarea>

					<v-alert v-if="saveError" type="error" dense text class="mb-4">
						{{ saveError }}
					</v-alert>

					<div class="d-flex">
						<v-btn
							tile
							depressed
							dark
							class="save-btn mr-3"
							:loading="saving"
							@click="saveIncident"
						>
							Save
						</v-btn>
						<v-btn outlined tile class="cancel-btn" :disabled="saving" @click="closeDrawer">
							Cancel
						</v-btn>
					</div>
				</v-form>
			</div>
		</v-navigation-drawer>
	</v-app>
</template>

<script>
import { riskAPI } from "../../api/csr/risk"

export default {
	name: "IncidentOverview",

	data() {
		return {
			loading: false,
			saving: false,
			saveError: "",
			search: "",
			typeFilter: "All types",
			statusFilter: "All statuses",
			incidents: [],
			drawer: false,
			selected: {},
			form: {
				consequence: "",
				action: "",
				responsible: "",
				status: "Open",
				reviewRoutine: "",
				trainingRoutine: ""
			},
			drawerStatusOptions: ["Open", "In progress", "Resolved"],
			headers: [
				{ text: "ID", value: "id", sortable: false, width: "120" },
				{ text: "TYPE", value: "type", sortable: false, width: "200" },
				{ text: "DATE", value: "date", sortable: false, width: "150" },
				{ text: "DESCRIPTION", value: "description", sortable: false },
				{ text: "STATUS", value: "status", sortable: false, width: "150" }
			]
		}
	},

	computed: {
		counts() {
			const count = s => this.incidents.filter(i => i.status === s).length
			return {
				open: count("Open"),
				inProgress: count("In progress"),
				resolved: count("Resolved")
			}
		},

		typeOptions() {
			return ["All types", ...new Set(this.incidents.map(i => i.type))]
		},

		statusOptions() {
			return ["All statuses", "Open", "In progress", "Resolved"]
		},

		filteredIncidents() {
			const q = this.search.trim().toLowerCase()
			return this.incidents.filter(i => {
				if (this.typeFilter !== "All types" && i.type !== this.typeFilter) return false
				if (this.statusFilter !== "All statuses" && i.status !== this.statusFilter)
					return false
				if (!q) return true
				return (
					i.id.toLowerCase().includes(q) ||
					i.type.toLowerCase().includes(q) ||
					i.description.toLowerCase().includes(q)
				)
			})
		}
	},

	created() {
		this.loadIncidents()
	},

	methods: {
		async loadIncidents() {
			this.loading = true
			try {
				const data = await riskAPI.getIncident(
					localStorage.getItem("incidentClientID")
				)
				// API returns [ [row, row, ...] ]
				const rows = (data && data[0]) || []
				this.incidents = rows.map(row => ({
					rawId: row.ID,
					id: "INC-" + String(row.ID).padStart(4, "0"),
					type: row.Type || "",
					date: row.Date ? String(row.Date).slice(0, 10) : "",
					description: row.Description || "",
					status: row.Status || "Open",
					consequence: row.Consequence || "",
					action: row.Action || "",
					responsible: row.Responsible || "",
					reviewRoutine: row.Review_Routine || "",
					trainingRoutine: row.Training_Routine || ""
				}))
			} catch (error) {
				console.error("Failed to load incidents:", error)
				this.incidents = []
			} finally {
				this.loading = false
			}
		},

		statusClass(status) {
			return {
				Open: "pill-open",
				"In progress": "pill-progress",
				Resolved: "pill-resolved"
			}[status]
		},

		// v-data-table @click:row passes (item, { select, isSelected, ... })
		openIncident(item) {
			this.selected = item
			this.form = {
				consequence: item.consequence,
				action: item.action,
				responsible: item.responsible,
				status: item.status,
				reviewRoutine: item.reviewRoutine,
				trainingRoutine: item.trainingRoutine
			}
			this.saveError = ""
			this.drawer = true
		},

		closeDrawer() {
			this.drawer = false
		},

		async saveIncident() {
			this.saving = true
			this.saveError = ""
			try {
				await riskAPI.updateIncident({
					clientID: localStorage.getItem("incidentClientID"),
					ID: this.selected.rawId,
					Consequence: this.form.consequence,
					Action: this.form.action,
					Responsible: this.form.responsible,
					Status: this.form.status,
					Review_Routine: this.form.reviewRoutine,
					Training_Routine: this.form.trainingRoutine
				})
				this.drawer = false
				await this.loadIncidents()
			} catch (error) {
				console.error("Failed to update incident:", error)
				this.saveError = error.message || "Failed to save incident."
			} finally {
				this.saving = false
			}
		},

		addIncident() {
			this.$router.push("/incidentreport/form")
		},

		logout() {
			localStorage.removeItem("incidentRole")
			localStorage.removeItem("incidentClientID")
			this.$router.push("/incidentreport")
		}
	}
}
</script>

<style scoped>
.header-bar {
	background: linear-gradient(90deg, #6aa9b4 0%, #5f9eab 100%) !important;
}

.header-bar .v-toolbar__content {
	position: relative;
}

.manager-view-title {
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	pointer-events: none;
}

.header-divider {
	width: 1px;
	height: 26px;
	background: rgba(255, 255, 255, 0.5);
}

.header-title {
	color: #ffffff;
	font-size: 1rem;
	font-weight: 500;
}

.add-btn {
	color: #ffffff !important;
	font-weight: 700;
	letter-spacing: 1.5px;
}

.logout-btn {
	color: #ffffff !important;
	border-color: rgba(255, 255, 255, 0.8) !important;
	font-weight: 700;
	letter-spacing: 1px;
}

.page-container {
	max-width: 1300px;
	padding-top: 40px;
}

.page-title {
	color: #3a7a85;
	font-size: 2.4rem;
	font-weight: 700;
}

.page-subtitle {
	color: #3a7a85;
	font-size: 1.15rem;
}

/* Summary cards */
.summary-row {
	display: flex;
	gap: 18px;
	flex-wrap: wrap;
}

.summary-card {
	width: 300px;
	background: #ffffff;
	border: 1px solid #d5e3e5;
	padding: 20px 24px;
}

.summary-card.open {
	border-left: 5px solid #b83a32;
}
.summary-card.progress {
	border-left: 5px solid #d9963c;
}
.summary-card.resolved {
	border-left: 5px solid #3b8a55;
}

.summary-count {
	font-size: 2.2rem;
	font-weight: 700;
	line-height: 1.1;
}
.open .summary-count {
	color: #b83a32;
}
.progress .summary-count {
	color: #d9963c;
}
.resolved .summary-count {
	color: #3b8a55;
}

.summary-label {
	color: #3b2a4d;
	font-size: 1rem;
	margin-top: 4px;
}

/* Filters */
.select-col {
	min-width: 190px;
}

::v-deep .filter-field .v-input__slot {
	border-radius: 2px;
}

/* Table */
.incident-table {
	border: 1px solid #d5e3e5;
	border-radius: 0 !important;
}

::v-deep .incident-table thead tr {
	background-color: #3d7a87;
}

::v-deep .incident-table thead th {
	background-color: #3d7a87 !important;
	color: #ffffff !important;
	font-size: 0.85rem !important;
	font-weight: 700;
	letter-spacing: 1px;
	height: 56px !important;
}

::v-deep .incident-table tbody tr {
	cursor: pointer;
}

::v-deep .incident-table tbody td {
	color: #3b2a4d;
	font-size: 1.05rem !important;
	padding-top: 14px !important;
	padding-bottom: 14px !important;
}

/* Status pills */
.status-pill {
	display: inline-block;
	padding: 4px 14px;
	border-radius: 999px;
	color: #ffffff;
	font-size: 0.85rem;
	font-weight: 600;
	white-space: nowrap;
}
.pill-open {
	background-color: #b83a32;
}
.pill-progress {
	background-color: #d9963c;
}
.pill-resolved {
	background-color: #3b8a55;
}
.pill-closed {
	background-color: #5b6770;
}

/* Detail drawer */
.detail-drawer {
	z-index: 300 !important;
}

.drawer-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	background: #66a6b4;
	color: #ffffff;
	padding: 22px 24px 20px;
}

.drawer-id {
	font-size: 1.6rem;
	font-weight: 700;
	line-height: 1.2;
}

.drawer-sub {
	font-size: 0.95rem;
	margin-top: 6px;
}

.drawer-body {
	padding: 28px 30px 40px;
}

.section-title {
	color: #3a7a85;
	font-size: 1.15rem;
	font-weight: 700;
	margin-bottom: 14px;
}

.reported-box {
	background: #f4f8f9;
	border: 1px solid #dbe6e8;
	padding: 16px 18px;
}

.field-label {
	color: #5b6770;
	font-size: 0.75rem;
	letter-spacing: 0.5px;
}

.field-value {
	color: #1e1e1e;
	font-size: 1rem;
	margin-bottom: 14px;
}

.form-label {
	display: block;
	color: #3a7a85;
	font-size: 0.9rem;
	font-weight: 700;
	margin-bottom: 6px;
}

::v-deep .detail-drawer .v-input__slot {
	border-radius: 2px;
}

.save-btn {
	background-color: #d9963c !important;
	font-weight: 700;
	letter-spacing: 2px;
	height: 48px !important;
	padding: 0 32px !important;
}

.cancel-btn {
	color: #3a7a85 !important;
	border-color: #cfdcdf !important;
	font-weight: 700;
	letter-spacing: 2px;
	height: 48px !important;
	padding: 0 32px !important;
}
</style>